import React, { useState } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { authAPI } from '@/lib/api';
import {
  LayoutDashboard, ShieldCheck, Shield, Wrench, BarChart3, ClipboardList,
  LogOut, Menu, ChevronRight, Key, Eye, EyeOff, Home, TrendingDown, MessageSquareWarning, FileWarning, ListChecks
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import { PageBreadcrumb } from '@/components/PageBreadcrumb';

const qualityNavItems = [
  { path: '/quality', icon: LayoutDashboard, label: 'Dashboard' },
  { path: '/quality/incoming-qc', icon: ShieldCheck, label: 'Incoming QC' },
  { path: '/quality/mrb', icon: Shield, label: 'MRB' },
  { path: '/quality/rejection-ppm', icon: TrendingDown, label: 'Rejection PPM' },
  { path: '/quality/complaints', icon: MessageSquareWarning, label: 'Customer Complaints' },
  { path: '/quality/internal-ncr', icon: FileWarning, label: 'Internal NC Register' },
  { path: '/quality/rejection-reasons', icon: ListChecks, label: 'Rejection Reasons' },
  { path: '/quality/calibration', icon: Wrench, label: 'Calibration' },
  { path: '/quality/calibration-report', icon: BarChart3, label: 'Cal. Reports' },
  { path: '/quality/audit-log', icon: ClipboardList, label: 'Audit Log' },
];

export default function QualityLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [changePasswordModal, setChangePasswordModal] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [changePasswordLoading, setChangePasswordLoading] = useState(false);

  const handleLogout = () => { logout(); navigate('/login'); };

  const handleChangePassword = async () => {
    if (newPassword !== confirmPassword) { toast.error('Passwords do not match'); return; }
    if (newPassword.length < 6) { toast.error('Password must be at least 6 characters'); return; }
    setChangePasswordLoading(true);
    try {
      await authAPI.changePassword(currentPassword, newPassword);
      toast.success('Password changed');
      setChangePasswordModal(false);
      setCurrentPassword(''); setNewPassword(''); setConfirmPassword('');
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setChangePasswordLoading(false); }
  };

  return (
    <div className="min-h-screen bg-background flex">
      {sidebarOpen && <div className="fixed inset-0 bg-black/50 z-20 lg:hidden" onClick={() => setSidebarOpen(false)} />}

      <aside className={`fixed lg:sticky top-0 left-0 z-30 h-screen w-60 bg-slate-50 border-r border-border flex flex-col transition-transform duration-200 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}`}>
        <div className="p-4 border-b border-border">
          <button onClick={() => navigate('/')} className="flex items-center gap-2 text-xs text-muted-foreground hover:text-foreground transition-colors" data-testid="back-to-erp">
            <Home className="w-3.5 h-3.5" /><span>ERP Home</span>
          </button>
          <h2 className="font-heading font-semibold text-base mt-2 tracking-tight flex items-center gap-2">
            <ShieldCheck className="w-4 h-4 text-teal-600" />
            Quality Mgmt
          </h2>
        </div>
        <nav className="flex-1 p-2 space-y-0.5 overflow-y-auto">
          {qualityNavItems.map(item => (
            <NavLink key={item.path} to={item.path} end={item.path === '/quality'}
              className={({ isActive }) => `flex items-center gap-2.5 px-3 py-2 rounded-md text-sm transition-colors ${isActive ? 'bg-teal-50 text-teal-700 font-medium border border-teal-200' : 'text-muted-foreground hover:bg-muted hover:text-foreground'}`}
              onClick={() => setSidebarOpen(false)} data-testid={`nav-${item.label.toLowerCase().replace(/[\s\/&.]/g, '-')}`}>
              <item.icon className="w-4 h-4 flex-shrink-0" />
              <span>{item.label}</span>
            </NavLink>
          ))}
        </nav>
        <div className="p-3 border-t border-border">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="w-full flex items-center gap-2 px-2 py-1.5 rounded-md hover:bg-muted transition-colors text-left" data-testid="user-menu-trigger">
                <div className="w-7 h-7 rounded-full bg-teal-100 text-teal-700 flex items-center justify-center text-xs font-semibold">
                  {user?.name?.charAt(0)?.toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-medium truncate">{user?.name}</p>
                  <p className="text-[10px] text-muted-foreground">{user?.role}</p>
                </div>
                <ChevronRight className="w-3 h-3 text-muted-foreground" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
              <DropdownMenuLabel className="text-xs">{user?.email}</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => setChangePasswordModal(true)} className="text-xs"><Key className="w-3 h-3 mr-2" />Change Password</DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleLogout} className="text-xs text-red-600"><LogOut className="w-3 h-3 mr-2" />Logout</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </aside>

      <main className="flex-1 min-w-0">
        <div className="lg:hidden p-3 border-b border-border flex items-center gap-3 bg-background sticky top-0 z-10">
          <Button variant="ghost" size="icon" onClick={() => setSidebarOpen(true)} data-testid="mobile-menu-toggle">
            <Menu className="w-5 h-5" />
          </Button>
          <span className="font-heading font-semibold text-sm">Quality Management</span>
        </div>
        <div className="p-4 md:p-6"><PageBreadcrumb /><Outlet /></div>
      </main>

      <Dialog open={changePasswordModal} onOpenChange={setChangePasswordModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Change Password</DialogTitle>
            <DialogDescription>Enter your current and new password</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Current Password</Label>
              <div className="relative">
                <Input type={showCurrentPassword ? "text" : "password"} value={currentPassword} onChange={e => setCurrentPassword(e.target.value)} className="pr-8" />
                <button className="absolute right-2 top-1/2 -translate-y-1/2" onClick={() => setShowCurrentPassword(!showCurrentPassword)}>
                  {showCurrentPassword ? <EyeOff className="w-3.5 h-3.5 text-muted-foreground" /> : <Eye className="w-3.5 h-3.5 text-muted-foreground" />}
                </button>
              </div>
            </div>
            <div>
              <Label className="text-xs">New Password</Label>
              <div className="relative">
                <Input type={showNewPassword ? "text" : "password"} value={newPassword} onChange={e => setNewPassword(e.target.value)} className="pr-8" />
                <button className="absolute right-2 top-1/2 -translate-y-1/2" onClick={() => setShowNewPassword(!showNewPassword)}>
                  {showNewPassword ? <EyeOff className="w-3.5 h-3.5 text-muted-foreground" /> : <Eye className="w-3.5 h-3.5 text-muted-foreground" />}
                </button>
              </div>
            </div>
            <div>
              <Label className="text-xs">Confirm New Password</Label>
              <Input type="password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setChangePasswordModal(false)}>Cancel</Button>
            <Button onClick={handleChangePassword} disabled={changePasswordLoading}>{changePasswordLoading ? 'Changing...' : 'Change Password'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
